/**
 * Form validation module
 * Client-side validation helpers for forms
 */

/**
 * Validation rules
 */
const rules = {
    required: (value) => {
        return value !== null && value !== undefined && value.toString().trim() !== '';
    },
    
    email: (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !value || emailRegex.test(value);
    },
    
    minLength: (value, length) => {
        return !value || value.length >= length;
    },
    
    maxLength: (value, length) => {
        return !value || value.length <= length;
    },
    
    pattern: (value, pattern) => {
        const regex = new RegExp(pattern);
        return !value || regex.test(value);
    },
    
    numeric: (value) => {
        return !value || !isNaN(value);
    },
    
    min: (value, min) => {
        return !value || parseFloat(value) >= parseFloat(min);
    },
    
    max: (value, max) => {
        return !value || parseFloat(value) <= parseFloat(max);
    },
    
    phone: (value) => {
        // Latvian phone format: +371 XXXXXXXX or XXXXXXXX
        const phoneRegex = /^(\+371\s?)?[2-9]\d{7}$/;
        return !value || phoneRegex.test(value.replace(/\s/g, ''));
    },
    
    url: (value) => {
        try {
            new URL(value);
            return true;
        } catch {
            return false;
        }
    }
};

/**
 * Error messages
 */
const messages = {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    minLength: 'Must be at least {0} characters',
    maxLength: 'Must be no more than {0} characters',
    pattern: 'Invalid format',
    numeric: 'Must be a number',
    min: 'Must be at least {0}',
    max: 'Must be no more than {0}',
    phone: 'Please enter a valid phone number',
    url: 'Please enter a valid URL'
};

/**
 * Validate a single field
 * @param {HTMLElement} field - Field to validate
 * @returns {Object} Validation result {valid: boolean, errors: string[]}
 */
export function validateField(field) {
    const errors = [];
    const value = field.value;
    
    // Check required
    if (field.hasAttribute('required') && !rules.required(value)) {
        errors.push(messages.required);
    }
    
    // Skip other validations if field is empty and not required
    if (!value && !field.hasAttribute('required')) {
        return { valid: true, errors: [] };
    }
    
    // Check type validations
    const type = field.type;
    if (type === 'email' && !rules.email(value)) {
        errors.push(messages.email);
    } else if (type === 'url' && !rules.url(value)) {
        errors.push(messages.url);
    } else if (type === 'tel' && !rules.phone(value)) {
        errors.push(messages.phone);
    } else if (type === 'number' && !rules.numeric(value)) {
        errors.push(messages.numeric);
    }
    
    // Check length validations
    if (field.hasAttribute('minlength')) {
        const minLength = parseInt(field.getAttribute('minlength'));
        if (!rules.minLength(value, minLength)) {
            errors.push(messages.minLength.replace('{0}', minLength));
        }
    }
    
    if (field.hasAttribute('maxlength')) {
        const maxLength = parseInt(field.getAttribute('maxlength'));
        if (!rules.maxLength(value, maxLength)) {
            errors.push(messages.maxLength.replace('{0}', maxLength));
        }
    }
    
    // Check numeric validations
    if (field.hasAttribute('min')) {
        const min = field.getAttribute('min');
        if (!rules.min(value, min)) {
            errors.push(messages.min.replace('{0}', min));
        }
    }
    
    if (field.hasAttribute('max')) {
        const max = field.getAttribute('max');
        if (!rules.max(value, max)) {
            errors.push(messages.max.replace('{0}', max));
        }
    }
    
    // Check pattern validation
    if (field.hasAttribute('pattern')) {
        const pattern = field.getAttribute('pattern');
        if (!rules.pattern(value, pattern)) {
            const customMessage = field.getAttribute('data-pattern-message') || messages.pattern;
            errors.push(customMessage);
        }
    }
    
    // Check custom validations
    const customValidation = field.getAttribute('data-validate');
    if (customValidation && rules[customValidation]) {
        const customParams = field.getAttribute('data-validate-params');
        const params = customParams ? customParams.split(',') : [];
        
        if (!rules[customValidation](value, ...params)) {
            const customMessage = field.getAttribute('data-validate-message') || messages[customValidation];
            errors.push(customMessage.replace(/\{(\d+)\}/g, (match, index) => params[index] || ''));
        }
    }
    
    return {
        valid: errors.length === 0,
        errors: errors
    };
}

/**
 * Show field errors
 * @param {HTMLElement} field - Field element
 * @param {string[]} errors - Error messages
 */
export function showFieldErrors(field, errors) {
    // Remove existing errors
    clearFieldErrors(field);
    
    if (errors.length === 0) return;
    
    // Add invalid class
    field.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500');
    
    // Create error container
    const errorContainer = document.createElement('div');
    errorContainer.className = 'form-error mt-1';
    errorContainer.setAttribute('role', 'alert');
    errorContainer.setAttribute('aria-live', 'polite');
    
    // Add error messages
    errors.forEach(error => {
        const errorElement = document.createElement('p');
        errorElement.className = 'text-sm text-red-600';
        errorElement.textContent = error;
        errorContainer.appendChild(errorElement);
    });
    
    // Insert after field or field group
    const fieldGroup = field.closest('.form-group') || field.parentElement;
    fieldGroup.appendChild(errorContainer);
}

/**
 * Clear field errors
 * @param {HTMLElement} field - Field element
 */
export function clearFieldErrors(field) {
    // Remove invalid classes
    field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500');
    
    // Remove error messages
    const fieldGroup = field.closest('.form-group') || field.parentElement;
    const errorContainer = fieldGroup.querySelector('.form-error');
    if (errorContainer) {
        errorContainer.remove();
    }
}

/**
 * Validate entire form
 * @param {HTMLFormElement} form - Form to validate
 * @returns {Object} Validation result {valid: boolean, errors: Object}
 */
export function validateForm(form) {
    const fields = form.querySelectorAll('input, select, textarea');
    const errors = {};
    let valid = true;
    
    fields.forEach(field => {
        if (field.disabled || field.type === 'hidden') return;
        
        const result = validateField(field);
        if (!result.valid) {
            valid = false;
            errors[field.name || field.id] = result.errors;
            showFieldErrors(field, result.errors);
        } else {
            clearFieldErrors(field);
        }
    });
    
    return { valid, errors };
}

/**
 * Initialize form validation
 * @param {HTMLFormElement} form - Form element
 * @param {Object} options - Validation options
 */
export function initFormValidation(form, options = {}) {
    const {
        validateOnBlur = true,
        validateOnInput = false,
        validateOnSubmit = true,
        onValid = null,
        onInvalid = null
    } = options;
    
    // Get all form fields
    const fields = form.querySelectorAll('input, select, textarea');
    
    // Add blur validation
    if (validateOnBlur) {
        fields.forEach(field => {
            field.addEventListener('blur', () => {
                const result = validateField(field);
                if (!result.valid) {
                    showFieldErrors(field, result.errors);
                } else {
                    clearFieldErrors(field);
                }
            });
        });
    }
    
    // Add input validation
    if (validateOnInput) {
        fields.forEach(field => {
            field.addEventListener('input', () => {
                // Only validate if field has been touched (has errors shown)
                const hasErrors = field.classList.contains('border-red-500');
                if (hasErrors) {
                    const result = validateField(field);
                    if (!result.valid) {
                        showFieldErrors(field, result.errors);
                    } else {
                        clearFieldErrors(field);
                    }
                }
            });
        });
    }
    
    // Add submit validation
    if (validateOnSubmit) {
        form.addEventListener('submit', (e) => {
            const result = validateForm(form);
            
            if (!result.valid) {
                e.preventDefault();
                
                // Focus first invalid field
                const firstInvalid = form.querySelector('.border-red-500');
                if (firstInvalid) {
                    firstInvalid.focus();
                }
                
                if (onInvalid) {
                    onInvalid(result.errors);
                }
            } else {
                if (onValid) {
                    onValid(e);
                }
            }
        });
    }
}

/**
 * Add custom validation rule
 * @param {string} name - Rule name
 * @param {Function} validator - Validator function
 * @param {string} message - Error message
 */
export function addValidationRule(name, validator, message) {
    rules[name] = validator;
    messages[name] = message;
}